/**
 * CONTENUIA STUDIO — CORE ENGINE V1→V3
 * Orchestrateur modulaire pour production vidéo marketing
 */
import { generateScript, generateYouTubeMetadata, callTTS, fetchPexelsVideo, VOIX_IA, VIDEO_STYLES, TONS_CONTENU } from './aiService';
import { mergeAllMultiScene } from './VideoEngine';

const WORKER = 'https://contenuia-proxy.soihibaliousseni.workers.dev';

export type ModuleStatus = 'active' | 'disabled';
export interface StudioModule { id: string; nom: string; version: 'V1' | 'V2' | 'V3'; status: ModuleStatus; description: string; }

export const STUDIO_MODULES: StudioModule[] = [
  { id: 'script', nom: 'Script IA', version: 'V1', status: 'active', description: 'Écriture du script avec Groq' },
  { id: 'storyboard', nom: 'Storyboard', version: 'V1', status: 'active', description: 'Découpage en scènes' },
  { id: 'voix', nom: 'Voix IA', version: 'V1', status: 'active', description: 'Google TTS Neural2 — 15 voix' },
  { id: 'soustitres', nom: 'Sous-titres', version: 'V1', status: 'active', description: 'Sous-titres animés' },
  { id: 'musique', nom: 'Musique', version: 'V1', status: 'active', description: 'Musique Pixabay' },
  { id: 'export', nom: 'Export MP4', version: 'V1', status: 'active', description: 'Assemblage vidéo' },
  { id: 'metadata', nom: 'Métadonnées', version: 'V1', status: 'active', description: 'Titre, description, hashtags' },
  { id: 'video_engine', nom: 'Video Generation Engine', version: 'V2', status: 'disabled', description: 'Runway / Kling / Pika' },
  { id: 'clips_ia', nom: 'Clips IA', version: 'V2', status: 'disabled', description: 'Clips, personnages, décors' },
  { id: 'realisateur', nom: 'Réalisateur IA', version: 'V3', status: 'disabled', description: 'Multi-caméra, plans auto' },
  { id: 'avatar', nom: 'Avatars IA', version: 'V3', status: 'disabled', description: 'HeyGen/D-ID' },
  { id: 'effets', nom: 'Effets cinéma', version: 'V3', status: 'disabled', description: 'Effets visuels' },
  { id: 'optimisation', nom: 'Optimisation Pub', version: 'V3', status: 'disabled', description: 'A/B testing' },
  { id: 'analytics', nom: 'Analytics', version: 'V3', status: 'disabled', description: 'Analyse performance' },
  { id: 'publisher', nom: 'Publisher', version: 'V3', status: 'disabled', description: 'Multi-réseau' },
];

export interface StudioBrief {
  produit: string; description: string; objectif: string; plateforme: string;
  duree: string; style: string; langue: string; voix: string;
  musique: boolean; musiqueMood: string;
}

export interface Scene { index: number; texte: string; duree: number; queryVisuel: string; }
export interface SceneVideo { url: string; thumbnail: string; query: string; }

export interface StudioResult {
  script: string; scenes: Scene[]; sceneVideos: SceneVideo[];
  audioB64: string; videoBlob: Blob | null; videoUrl: string;
  metadata: { titre: string; description: string; tags: string[] };
  dureeReelle: number; voixLabel: string; musiqueMood: string; promptEnvoye: string;
}

export type ProgressCallback = (etape: string, pct: number, module: string) => void;

export const STUDIO_OBJECTIFS = [
  { id: 'vendre', label: 'Vendre', icon: '💰', desc: 'Conversion, achat' },
  { id: 'recruter', label: 'Recruter', icon: '🤝', desc: 'Candidats, partenaires' },
  { id: 'former', label: 'Former', icon: '🎓', desc: 'Expliquer, enseigner' },
  { id: 'notoriete', label: 'Notoriété', icon: '🚀', desc: 'Faire connaître' },
  { id: 'saas', label: 'SaaS', icon: '💎', desc: 'Promouvoir un outil' },
];

export const STUDIO_PLATEFORMES = [
  { id: 'tiktok', label: 'TikTok', icon: '🎵' },
  { id: 'youtube', label: 'YouTube Shorts', icon: '🔴' },
  { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
  { id: 'instagram', label: 'Instagram Reels', icon: '📸' },
  { id: 'facebook', label: 'Facebook', icon: '📘' },
  { id: 'x', label: 'X (Twitter)', icon: '𝕏' },
  { id: 'pinterest', label: 'Pinterest', icon: '📌' },
  { id: 'snapchat', label: 'Snapchat', icon: '👻' },
];

function decouperEnScenes(script: string, dureeTotale: number): Scene[] {
  const cleaned = script.replace(/\[.*?\]/g, '').replace(/HOOK.*?:|PROBLEME.*?:|SOLUTION.*?:|BENEFICE.*?:|CTA.*?:/gi, '').replace(/\n+/g, ' ').trim();
  const phrases = cleaned.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 3);
  if (!phrases.length) return [];
  const dps = dureeTotale / phrases.length;
  const visuels = ['business professional office', 'entrepreneur success laptop', 'marketing digital technology', 'startup team meeting', 'creative work modern', 'city business lifestyle', 'innovation future tech'];
  return phrases.slice(0, 8).map((texte, i) => ({ index: i, texte, duree: dps, queryVisuel: visuels[i % visuels.length] }));
}

/** Erreur de production Studio, avec message lisible pour l'utilisateur. */
export class StudioError extends Error {
  constructor(message: string, public etape: string) {
    super(message);
    this.name = 'StudioError';
  }
}

export async function studioProduce(brief: StudioBrief, onProgress: ProgressCallback): Promise<StudioResult> {
  // --- Validation des entrées ---
  if (!brief.produit?.trim()) {
    throw new StudioError('Renseignez le nom du produit avant de générer.', 'validation');
  }

  const styleLabel = VIDEO_STYLES[brief.style as keyof typeof VIDEO_STYLES]?.label || 'YouTube Shorts';
  const ton = TONS_CONTENU[Math.floor(Math.random() * TONS_CONTENU.length)];
  const dureeNum = parseInt(brief.duree) || 30;

  // ---- ÉTAPE 1 : SCRIPT ----
  let script: string;
  try {
    onProgress('Écriture du script (' + ton + ')...', 8, 'script');
    script = await generateScript({
      produit: brief.produit,
      duree: brief.duree,
      format: styleLabel,
      langue: brief.langue,
      message: brief.description || brief.objectif,
      persona: 'mrbeast',
    });
  } catch (e: any) {
    throw new StudioError(e?.message || "Le script n'a pas pu être généré. Réessayez.", 'script');
  }

  // ---- ÉTAPE 2 : STORYBOARD ----
  onProgress('Découpage en scènes...', 20, 'storyboard');
  const scenes = decouperEnScenes(script, dureeNum);
  if (!scenes.length) {
    throw new StudioError('Le script est trop court pour être découpé en scènes.', 'storyboard');
  }

  // ---- ÉTAPE 3 : VOIX ----
  let audioB64: string;
  const voix = VOIX_IA[brief.voix] || VOIX_IA['fr-femme-1'];
  try {
    onProgress('Génération de la voix...', 32, 'voix');
    audioB64 = await callTTS(script.replace(/\[.*?\]/g, ''), voix.languageCode, voix.voiceName);
  } catch (e: any) {
    throw new StudioError(e?.message || "La voix IA n'a pas pu être générée.", 'voix');
  }

  onProgress('Préparation des sous-titres...', 45, 'soustitres');

  // ---- ÉTAPE 4 : VISUELS (tolérant : au moins 1 vidéo suffit) ----
  onProgress('Sélection des scènes vidéo...', 52, 'export');
  const sceneVideos: SceneVideo[] = [];
  const sceneQueries = scenes.slice(0, 5).map(s => s.queryVisuel || brief.produit + ' business');

  await Promise.all(
    sceneQueries.map(async (query, i) => {
      const v = await fetchPexelsVideo(query);
      if (v?.url) sceneVideos[i] = { url: v.url, thumbnail: v.thumbnail, query };
    })
  );

  // Fallback si certaines scènes n'ont rien trouvé
  let fallback: SceneVideo | null = null;
  if (sceneVideos.filter(Boolean).length < scenes.length) {
    const fb = await fetchPexelsVideo(brief.produit + ' business professional');
    if (fb?.url) fallback = { url: fb.url, thumbnail: fb.thumbnail, query: brief.produit };
  }

  const valides = sceneVideos.filter(Boolean);
  if (!valides.length && !fallback) {
    throw new StudioError("Aucune vidéo n'a été trouvée pour ce produit. Essayez un autre nom de produit.", 'visuels');
  }

  const remplissage = fallback || valides[0];
  for (let i = 0; i < scenes.length; i++) {
    if (!sceneVideos[i]) sceneVideos[i] = remplissage;
  }

  // ---- ÉTAPE 5 : ASSEMBLAGE ----
  let videoBlob: Blob;
  try {
    onProgress('Assemblage vidéo multi-scènes...', 68, 'export');
    const proxyUrls = sceneVideos
      .map(sv => (sv?.url ? WORKER + '/proxy-video?url=' + encodeURIComponent(sv.url) : ''))
      .filter(Boolean);
    videoBlob = await mergeAllMultiScene({
      sceneUrls: proxyUrls,
      sceneDurations: scenes.map(s => s.duree),
      audioB64,
      script,
      produit: brief.produit,
      onProgress: (s, p) => onProgress(s, 68 + Math.round(p * 0.25), 'export'),
    });
  } catch (e: any) {
    throw new StudioError(
      e?.message || "Le montage vidéo a échoué. Utilisez Chrome ou Edge (version récente).",
      'montage'
    );
  }

  // ---- ÉTAPE 6 : MÉTADONNÉES (non bloquant) ----
  let metadata = { titre: brief.produit, description: '', tags: [brief.produit] as string[] };
  try {
    onProgress('Génération des métadonnées...', 95, 'metadata');
    metadata = await generateYouTubeMetadata({
      produit: brief.produit,
      cible: brief.objectif,
      script,
      langue: brief.langue,
    });
  } catch (e) {
    console.warn('[studioEngine] Métadonnées échouées, valeurs par défaut utilisées:', e);
  }

  onProgress('Vidéo prête !', 100, 'metadata');

  return {
    script,
    scenes,
    sceneVideos,
    audioB64,
    videoBlob,
    videoUrl: videoBlob ? URL.createObjectURL(videoBlob) : '',
    metadata,
    dureeReelle: Math.max(audioB64.length / 22000, dureeNum),
    voixLabel: voix.label || brief.voix,
    musiqueMood: brief.musiqueMood,
    promptEnvoye: brief.description || brief.objectif,
  };
}
